import { ExemptionQuestions } from "utils/types/exemption-questions";

import PurchaseDate from "./questions/PurchaseDate";
import AdditionalOwner from "./questions/AdditionalOwner";
import Agreement from "./questions/Agreement";
import CurrentlyReceiving from "./questions/CurrentlyReceiving";
import Dob from "./questions/Dob";
import HomesteadQualified from "./questions/HomesteadQualified";
import Intro from "./questions/Intro";
import MainConfirmation from "./questions/MainConfirmation";
import NonAgreementConfirmation from "./questions/NonAgreementConfirmation";
import OtherCounties from "./questions/OtherCounties";
import Owner2DL from "./questions/Owner2DL";
import OwnerDL from "./questions/OwnerDL";
import Owners from "./questions/Owners";
import MaritalStatus from "./questions/MaritalStatus";
import OwnershipPercent from "./questions/OwnershipPercent";
import PreviousResidence from "./questions/PreviousResidence";
import PrimaryResidence from "./questions/PrimaryResidence";
import RareSituations from "./questions/RareSituations";
import SqFt from "./questions/SqFt";
import TransferringHomestead from "./questions/TransferringHomestead";
import ManufacturedHome from "./questions/ManufacturedHome";
import TransferMoveSaleDate from "./questions/TransferMoveSaleDate";
import MoveInDate from "./questions/MoveInDate";
import { SsnTin, SsnTin2 } from "./questions/SsnTin";
import {
	DriversLicenseNumber,
	DriversLicenseNumber2,
} from "./questions/DriversLicenseNumber";
import { ILOnHoldDisclaimer } from "./questions/ILOnHoldDisclaimer";
import VehicleRegistrations from "./questions/VehicleRegistrations";
import VehicleInfo from "./questions/VehicleInfo";
import {
	TaxFilingHistory,
	TaxFilingHistory2,
	TaxFilingHistoryNYLastFiveYears,
} from "./questions/TaxFilingHistory";
import {
	TaxFilingHistoryDetails,
	TaxFilingHistoryDetails2,
	TaxFilingHistoryDetailsNYLastFiveYears,
} from "./questions/TaxFilingHistoryDetails";
import AnnualHouseholdIncome from "./questions/AnnualHouseholdIncome";
import { FileUploadType } from "elements/Input/FileUpload";
import Portability from "./questions/Portability";
import UsCitizen from "./questions/UsCitizen";
import TransferExemptionReason from "./questions/TransferExemptionReason";
import ResidencyProof from "./questions/ResidencyProof";
import OutOfStateTaxReturn from "./questions/OutOfStateTaxReturn";
import PreviousAddressSearch from "./questions/PreviousAddressSearch";
import SchoolDistrict from "./questions/SchoolDistrict";
import FarmLand from "./questions/FarmLand";
import IncomeProducing from "./questions/IncomeProducing";
import CoOp from "./questions/CoOp";
import {
	PercentOfSquareFootageUsedAsBusinessOrRentalProperty,
	PercentOfCoOpOwnership,
} from "./questions/SpecialFieldPercent";
import CurrentEmployer from "./questions/CurrentEmployer";
import PreviousTaxReturnAddress from "./questions/PreviousTaxReturnAddress";
import HasPortalAccount from "./questions/HasPortalAccount";
import PortalAccountCredentials from "./questions/PortalAccountCredentials";
import NARResolved from "./questions/NARResolved";
import AlreadyFiled from "./questions/AlreadyFiled";
import TrustDocumentUpload from "./questions/TrustDocumentUpload";

export const valueIsQuestionId = (
	v: any
): v is ExemptionQuestions.QuestionId => {
	try {
		return ExemptionQuestions.EXEMPTION_QUESTION_IDS.includes(
			v as ExemptionQuestions.QuestionId
		);
	} catch {
		return false;
	}
};

export const getQuestionComponent = (id: ExemptionQuestions.QuestionId) => {
	const EXEMPTION_QUESTION_COMPONENT_MAP: {
		[key in ExemptionQuestions.QuestionId]: React.FC<{
			ExitButton?: React.FC;
		}>;
	} = {
		INTRO: Intro,
		PRIMARY_RESIDENCE: PrimaryResidence,
		CURRENTLY_RECEIVING: CurrentlyReceiving,
		TRANSFERRING_HOMESTEAD: TransferringHomestead,
		PURCHASE_DATE: PurchaseDate,
		MOVE_IN_DATE: MoveInDate,
		RARE_SITUATIONS: RareSituations,
		PREVIOUS_RESIDENCE: PreviousResidence,
		PORTABILITY: Portability,
		US_CITIZEN: UsCitizen,
		HOMESTEAD_QUALIFIED: HomesteadQualified,
		DOB: Dob,
		SQFT: SqFt,
		OWNERS: Owners,
		MARITAL_STATUS: MaritalStatus,
		ADDITIONAL_OWNER: AdditionalOwner,
		SSN_TIN: SsnTin,
		SSN_TIN2: SsnTin2,
		DL_UPLOAD: OwnerDL,
		DL2_UPLOAD: Owner2DL,
		IL_ON_HOLD_DISCLAIMER: ILOnHoldDisclaimer,
		DRIVERS_LICENSE_NUMBER: DriversLicenseNumber,
		DRIVERS_LICENSE_NUMBER2: DriversLicenseNumber2,
		MANUFACTURED_HOME: ManufacturedHome,
		OTHER_COUNTIES: OtherCounties,
		OWNERSHIP_PERCENT: OwnershipPercent,
		NON_AGREEMENT_CONFIRMATION: NonAgreementConfirmation,
		AGREEMENT: Agreement,
		MAIN_CONFIRMATION: MainConfirmation,
		TRANSFER_MOVE_SALE_DATE: TransferMoveSaleDate,
		VEHICLE_REGISTRATIONS: VehicleRegistrations,
		VEHICLE_INFO: VehicleInfo,
		// TRANSFER_STAR_CREDIT: TransferStarCredit,
		TRANSFER_EXEMPTION_REASON: TransferExemptionReason,
		RESIDENCY_PROOF: ResidencyProof,
		TAX_FILING_HISTORY: TaxFilingHistory,
		TAX_FILING_HISTORY_DETAILS: TaxFilingHistoryDetails,
		TAX_FILING_HISTORY_2: TaxFilingHistory2,
		TAX_FILING_HISTORY_DETAILS_2: TaxFilingHistoryDetails2,
		TAX_FILING_HISTORY_NY_LAST_5_YEARS: TaxFilingHistoryNYLastFiveYears,
		TAX_FILING_HISTORY_NY_LAST_5_YEARS_DETAILS:
			TaxFilingHistoryDetailsNYLastFiveYears,
		OUT_OF_STATE_TAX_RETURN: OutOfStateTaxReturn,
		TRUST_DOCUMENT_UPLOAD: TrustDocumentUpload,
		ANNUAL_HOUSEHOLD_INCOME: AnnualHouseholdIncome,
		PREVIOUS_ADDRESS_SEARCH: PreviousAddressSearch,
		SCHOOL_DISTRICT: SchoolDistrict,
		FARM_LAND: FarmLand,
		INCOME_PRODUCING: IncomeProducing,
		CO_OP: CoOp,
		PERCENT_OF_SQUARE_FOOTAGE_USED_AS_BUSINESS_OR_RENTAL_PROPERTY:
			PercentOfSquareFootageUsedAsBusinessOrRentalProperty,
		PERCENT_OF_CO_OP_OWNERSHIP: PercentOfCoOpOwnership,
		CURRENT_EMPLOYER: CurrentEmployer,
		PREVIOUS_TAX_RETURN_ADDRESS: PreviousTaxReturnAddress,
		HAS_PORTAL_ACCOUNT: HasPortalAccount,
		PORTAL_ACCOUNT_CREDENTIALS: PortalAccountCredentials,
		NAR_RESOLVED: NARResolved,
		ALREADY_FILED: AlreadyFiled,
	};
	return EXEMPTION_QUESTION_COMPONENT_MAP[id];
};
